// The browser half of Google Sheets import: the sign-in popup whose code the
// backend trades for a stored grant, and the Picker that grants Muva the one
// sheet picked (drive.file sees nothing else). Google's scripts load on first
// use. Until the app's Google keys are set, none of it is offered

import { projectNumber } from './firebase';

const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
const pickerKey = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;

export const isGoogleConfigured = !!clientId && !!pickerKey;

const IDENTITY_URL = 'https://accounts.google.com/gsi/client';
const PICKER_URL = 'https://apis.google.com/js/api.js';
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

const scripts = new Map();

const loadScript = (src) => {
  if (!scripts.has(src))
    scripts.set(
      src,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => {
          scripts.delete(src);
          reject(new Error('Google did not load. Try again'));
        };
        document.head.appendChild(script);
      }),
    );
  return scripts.get(src);
};

// Loaded ahead of the click: a popup opened after an await can be blocked
export const prepareGoogleSignIn = () =>
  loadScript(IDENTITY_URL).catch(() => {});

// The popup's authorization code, or null when the person closed it
export const requestGoogleCode = async () => {
  if (!window.google?.accounts?.oauth2) await loadScript(IDENTITY_URL);
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: DRIVE_FILE_SCOPE,
      ux_mode: 'popup',
      callback: (response) => {
        if (response.code) resolve(response.code);
        else
          reject(
            new Error(
              response.error_description ?? 'Google sign-in did not go through',
            ),
          );
      },
      error_callback: (error) => {
        if (error?.type === 'popup_closed') resolve(null);
        else reject(new Error('Google sign-in was blocked. Allow pop-ups'));
      },
    });
    client.requestCode();
  });
};

// Opens on the linked sheet alone: picking it is what grants Muva access.
// True once picked, false when the person cancels
export const pickGoogleFile = async ({ fileId, accessToken }) => {
  await loadScript(PICKER_URL);
  await new Promise((resolve, reject) =>
    window.gapi.load('picker', { callback: resolve, onerror: reject }),
  );
  const { picker } = window.google;
  return new Promise((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setFileIds(fileId)
      .setMode(picker.DocsViewMode.LIST);
    new picker.PickerBuilder()
      .setAppId(projectNumber)
      .setOAuthToken(accessToken)
      .setDeveloperKey(pickerKey)
      .addView(view)
      .setTitle('Pick the schedule sheet to share it with Muva')
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) resolve(true);
        else if (data.action === picker.Action.CANCEL) resolve(false);
      })
      .build()
      .setVisible(true);
  });
};
